import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'

import {referencesAreEqual} from './copilot-chat-helpers'
import type {CopilotChatReference, SkillExecution} from './copilot-chat-types'

/**
 * Computes the references array (and resulting implicit-reference set) produced by replacing
 * `referenceToDelete` with `referenceToInsert` within `currentReferences`. Shared between the
 * reducer's REPLACE_REFERENCE case and telemetry so the two stay in sync without duplicating logic.
 */
export function computeReplacedReferences(
  currentReferences: CopilotChatReference[],
  implicitReferences: ReadonlySet<CopilotChatReference>,
  referenceToDelete: CopilotChatReference,
  referenceToInsert: CopilotChatReference,
): {updatedReferences: CopilotChatReference[]; implicitReferences: ReadonlySet<CopilotChatReference>} {
  const updatedReferences: CopilotChatReference[] = []
  let newImplicitReferences: ReadonlySet<CopilotChatReference> = implicitReferences
  for (const reference of currentReferences) {
    if (referencesAreEqual(reference, referenceToDelete)) {
      updatedReferences.push(referenceToInsert)
      if (newImplicitReferences.has(reference)) {
        const next = new Set(newImplicitReferences)
        next.delete(reference)
        next.add(referenceToInsert)
        newImplicitReferences = next
      }
    }
    // Make sure we can't end up with two references that have the same ID in the resulting array
    else if (!referencesAreEqual(reference, referenceToInsert)) updatedReferences.push(reference)
  }
  return {updatedReferences, implicitReferences: newImplicitReferences}
}

/**
 * Finds the index of the skill execution that an incoming function-call event applies to.
 * Uses strict callId matching when the relevant flags are enabled, otherwise falls back to the
 * first in-flight execution. Shared between the reducer and telemetry.
 */
export function findMatchingExecutionIndex(prevExecutions: SkillExecution[], action: {callId?: string}): number {
  const useStrictCallIdMatching = copilotFeatureFlags.enableToolCallLogs || copilotFeatureFlags.interspersedToolCalls
  if (action.callId && useStrictCallIdMatching) {
    return prevExecutions.findIndex(fc => fc.callId === action.callId)
  }
  return prevExecutions.findIndex(fc => fc.status === 'started' || fc.status === 'progress')
}

/**
 * Marks any in-flight (`started`/`progress`) skill executions as `completed`. CAPI never emits
 * explicit completed events for function calls, so we finalize them when streaming ends. Shared
 * between the reducer's MESSAGE_STREAMING_COMPLETED case and telemetry.
 */
export function finalizeStreamingExecutions(executions: SkillExecution[] | undefined): SkillExecution[] | undefined {
  if (!executions) return executions
  return executions.map(execution =>
    execution.status === 'started' || execution.status === 'progress' ? {...execution, status: 'completed'} : execution,
  )
}
